import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Chip,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Stack,
  Switch,
} from '@mui/material';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useTranslation } from 'react-i18next';
import { ecommerceApi } from '../../../config/apiClient';
import {
  getGeneralConfig,
  getMenuConfig,
  saveGeneralConfig,
  saveMenuConfig,
} from '../../../services/ecommerceApi';
import { notifyEcommerceSaved } from '../utils/ecommerceSaveToast';
import {
  parseCorporateSidebarSettingsJson,
  serializeCorporateSidebarSettingsJson,
} from '../utils/corporateSectionSettings';

interface SupportTicketMessage {
  id: string;
  senderType: string;
  senderUserId?: string | null;
  senderName?: string | null;
  body: string;
  createdAt: string;
}

interface SupportTicket {
  id: string;
  ticketNumber: string;
  subject: string;
  category: string;
  status: string;
  priority: string;
  memberId?: string | null;
  orderId?: string | null;
  customerName?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  contactName?: string | null;
  createdAt: string;
  updatedAt?: string | null;
  closedAt?: string | null;
  messages?: SupportTicketMessage[];
}

interface MemberOption {
  id: string;
  email: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
}

interface SupportManagementProps {
  companyId: string | null;
}

const STATUS_COLORS: Record<string, 'default' | 'info' | 'success' | 'warning' | 'error'> = {
  Open: 'info',
  InProgress: 'warning',
  WaitingCustomer: 'default',
  Resolved: 'success',
  Closed: 'default'
};

const CATEGORIES = ['General', 'Order', 'Payment', 'Shipping', 'Product', 'Other'] as const;
const PRIORITIES = ['Low', 'Normal', 'High'] as const;
const STATUSES = ['Open', 'InProgress', 'WaitingCustomer', 'Resolved', 'Closed'] as const;

const SupportManagement: React.FC<SupportManagementProps> = ({ companyId }) => {
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const tt = (tr: string, en: string) => (isTr ? tr : en);
  const localeCode = isTr ? 'tr-TR' : 'en-US';

  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [supportEnabled, setSupportEnabled] = useState(true);
  const [supportSettingLoading, setSupportSettingLoading] = useState(false);
  const [supportSettingSaving, setSupportSettingSaving] = useState(false);
  const supportSettingLoadedRef = useRef(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [replyBody, setReplyBody] = useState('');
  const [replySaving, setReplySaving] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [createSaving, setCreateSaving] = useState(false);
  const [members, setMembers] = useState<MemberOption[]>([]);
  const [createForm, setCreateForm] = useState({
    memberId: '',
    subject: '',
    message: '',
    category: 'General',
    priority: 'Normal',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    orderId: ''
  });

  const statusLabel = (status: string) => {
    const map: Record<string, [string, string]> = {
      Open: ['Açık', 'Open'],
      InProgress: ['İşlemde', 'In Progress'],
      WaitingCustomer: ['Müşteri Bekleniyor', 'Waiting Customer'],
      Resolved: ['Çözüldü', 'Resolved'],
      Closed: ['Kapalı', 'Closed']
    };
    const entry = map[status];
    return entry ? tt(entry[0], entry[1]) : status;
  };

  const categoryLabel = (category: string) => {
    const map: Record<string, [string, string]> = {
      General: ['Genel', 'General'],
      Order: ['Sipariş', 'Order'],
      Payment: ['Ödeme', 'Payment'],
      Shipping: ['Kargo', 'Shipping'],
      Product: ['Ürün', 'Product'],
      Other: ['Diğer', 'Other']
    };
    const entry = map[category];
    return entry ? tt(entry[0], entry[1]) : category;
  };

  const priorityLabel = (priority: string) => {
    const map: Record<string, [string, string]> = {
      Low: ['Düşük', 'Low'],
      Normal: ['Normal', 'Normal'],
      High: ['Yüksek', 'High']
    };
    const entry = map[priority];
    return entry ? tt(entry[0], entry[1]) : priority;
  };

  const formatDate = (value?: string | null) => {
    if (!value) return '-';
    return new Date(value).toLocaleDateString(localeCode, {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const loadTickets = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== 'all') params.set('status', statusFilter);
      if (searchTerm.trim()) params.set('search', searchTerm.trim());
      params.set('page', String(page + 1));
      params.set('pageSize', String(rowsPerPage));
      const qs = params.toString();
      const res = await ecommerceApi.get(`/api/ecommerce/support-tickets?${qs}`);
      const data = res.data?.data || [];
      setTickets(Array.isArray(data) ? data : []);
      setTotalCount(typeof res.data?.totalCount === 'number' ? res.data.totalCount : (Array.isArray(data) ? data.length : 0));
    } catch (err) {
      console.error('[SupportManagement] Talepler yüklenemedi:', err);
      setSnackbar({ open: true, message: tt('Destek talepleri yüklenemedi.', 'Failed to load support tickets.'), severity: 'error' });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, statusFilter, searchTerm, page, rowsPerPage]);

  useEffect(() => {
    if (companyId) loadTickets();
  }, [companyId, loadTickets]);

  useEffect(() => {
    const loadSupportSetting = async () => {
      if (!companyId) return;
      setSupportSettingLoading(true);
      supportSettingLoadedRef.current = false;
      try {
        // Production'da SupportEnabled kolonu olmayabilir → menü JSON'dan oku (kalıcı).
        const [menuResponse, generalResponse] = await Promise.all([
          getMenuConfig(companyId).catch(() => null),
          getGeneralConfig(companyId).catch(() => null),
        ]);

        const menuData = menuResponse?.data || null;
        const sidebar = parseCorporateSidebarSettingsJson(menuData?.corporateSidebarSettingsJson);
        const fromMenu = sidebar.supportTicketsEnabled;
        const fromGeneral = generalResponse?.data?.supportEnabled;

        if (fromMenu === false || fromGeneral === false) {
          setSupportEnabled(false);
        } else if (fromMenu === true || fromGeneral === true) {
          setSupportEnabled(true);
        } else {
          setSupportEnabled(true);
        }
        supportSettingLoadedRef.current = true;
      } catch (err) {
        console.error('[SupportManagement] Destek ayarı yüklenemedi:', err);
        setSupportEnabled(true);
        supportSettingLoadedRef.current = true;
      } finally {
        setSupportSettingLoading(false);
      }
    };

    loadSupportSetting();
  }, [companyId]);

  const handleSupportEnabledChange = async (checked: boolean) => {
    if (!companyId || !supportSettingLoadedRef.current) return;
    setSupportEnabled(checked);
    setSupportSettingSaving(true);
    try {
      const [menuResponse, generalResponse] = await Promise.all([
        getMenuConfig(companyId),
        getGeneralConfig(companyId).catch(() => null),
      ]);

      const menuData = menuResponse?.data;
      const configId =
        menuData?.configId ||
        generalResponse?.data?.id ||
        generalResponse?.data?.configId;

      if (!menuData || !configId) {
        throw new Error('Menu config or configId missing');
      }

      const sidebar = parseCorporateSidebarSettingsJson(menuData.corporateSidebarSettingsJson);
      const nextSidebarJson = serializeCorporateSidebarSettingsJson(
        {
          ...sidebar,
          supportTicketsEnabled: checked,
        },
        sidebar.layoutMode === 'below_navbar' ? 'below_navbar' : 'below_hero'
      );

      // Mevcut menü alanlarını koruyarak sadece JSON flag'ini güncelle (production-safe).
      await saveMenuConfig({
        companyId,
        configId,
        enabled: menuData.enabled,
        menuType: menuData.menuType,
        menuPosition: menuData.menuPosition,
        verticalPadding: menuData.verticalPadding,
        backgroundColor: menuData.backgroundColor,
        textColor: menuData.textColor,
        hoverBackgroundColor: menuData.hoverBackgroundColor,
        hoverTextColor: menuData.hoverTextColor,
        textTransform: menuData.textTransform,
        showAllCategories: menuData.showAllCategories,
        showHomePage: menuData.showHomePage,
        showQuoteButton: menuData.showQuoteButton,
        quoteButtonTitle: menuData.quoteButtonTitle,
        quoteButtonSubtitle: menuData.quoteButtonSubtitle,
        quoteButtonLink: menuData.quoteButtonLink,
        corporateNavDisplayMode: menuData.corporateNavDisplayMode,
        corporateSidebarLayoutMode: menuData.corporateSidebarLayoutMode,
        corporateSidebarSettingsJson: nextSidebarJson,
        showDropdownIcons: menuData.showDropdownIcons ?? menuData.showMenuLeftLogo,
        menuStyle: menuData.menuStyle,
        dropdownBackgroundColor: menuData.dropdownBackgroundColor,
        dropdownTextColor: menuData.dropdownTextColor,
        enableMegaMenu: menuData.enableMegaMenu,
        megaMenuColumns: menuData.megaMenuColumns,
        showMegaMenuImages: menuData.showMegaMenuImages,
        animationType: menuData.animationType,
        animationDuration: menuData.animationDuration,
        mobileBreakpoint: menuData.mobileBreakpoint,
      });

      // Yeni kolon varsa best-effort senkron (yoksa sessizce geç).
      try {
        await saveGeneralConfig({
          companyId,
          supportEnabled: checked,
        });
      } catch {
        // kolon/API yoksa menü JSON kaydı yeterli
      }

      notifyEcommerceSaved(
        tt('Destek görünürlüğü kaydedildi.', 'Support visibility saved.')
      );
    } catch (err) {
      console.error('[SupportManagement] Destek ayarı kaydedilemedi:', err);
      setSupportEnabled(!checked);
      setSnackbar({
        open: true,
        message: tt('Destek ayarı kaydedilemedi.', 'Failed to save support setting.'),
        severity: 'error',
      });
    } finally {
      setSupportSettingSaving(false);
    }
  };

  const loadMembers = async () => {
    try {
      const res = await ecommerceApi.get('/api/ecommerce/members');
      const data = res.data?.data || [];
      setMembers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('[SupportManagement] Üyeler yüklenemedi:', err);
    }
  };

  const openDetail = async (ticket: SupportTicket) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setReplyBody('');
    try {
      const res = await ecommerceApi.get(`/api/ecommerce/support-tickets/${ticket.id}`);
      setSelectedTicket(res.data?.data || ticket);
    } catch (err) {
      console.error('[SupportManagement] Detay yüklenemedi:', err);
      setSnackbar({ open: true, message: tt('Talep detayı yüklenemedi.', 'Failed to load ticket detail.'), severity: 'error' });
      setSelectedTicket(ticket);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleReply = async () => {
    if (!selectedTicket || !replyBody.trim()) return;
    setReplySaving(true);
    try {
      const res = await ecommerceApi.post(`/api/ecommerce/support-tickets/${selectedTicket.id}/messages`, {
        body: replyBody.trim(),
        senderName: 'Admin'
      });
      if (res.data?.success) {
        setSelectedTicket(res.data.data);
        setReplyBody('');
        setSnackbar({ open: true, message: tt('Yanıt gönderildi.', 'Reply sent.'), severity: 'success' });
        loadTickets();
      } else {
        throw new Error(res.data?.message);
      }
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || tt('Yanıt gönderilemedi.', 'Failed to send reply.'),
        severity: 'error'
      });
    } finally {
      setReplySaving(false);
    }
  };

  const handleStatusChange = async (status: string) => {
    if (!selectedTicket) return;
    setStatusSaving(true);
    try {
      const res = await ecommerceApi.post(`/api/ecommerce/support-tickets/${selectedTicket.id}/status`, { status });
      if (res.data?.success) {
        setSelectedTicket(res.data.data);
        setSnackbar({ open: true, message: tt('Durum güncellendi.', 'Status updated.'), severity: 'success' });
        loadTickets();
      } else {
        throw new Error(res.data?.message);
      }
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || tt('Durum güncellenemedi.', 'Failed to update status.'),
        severity: 'error'
      });
    } finally {
      setStatusSaving(false);
    }
  };

  const openCreate = async () => {
    setCreateForm({
      memberId: '',
      subject: '',
      message: '',
      category: 'General',
      priority: 'Normal',
      contactName: '',
      contactEmail: '',
      contactPhone: '',
      orderId: ''
    });
    setCreateOpen(true);
    await loadMembers();
  };

  const handleMemberSelect = (memberId: string) => {
    const member = members.find((m) => m.id === memberId);
    setCreateForm((prev) => ({
      ...prev,
      memberId,
      contactName: member
        ? (member.displayName || `${member.firstName || ''} ${member.lastName || ''}`.trim() || member.email)
        : prev.contactName,
      contactEmail: member?.email || prev.contactEmail,
      contactPhone: member?.phone || prev.contactPhone
    }));
  };

  const handleCreate = async () => {
    if (!createForm.subject.trim() || !createForm.message.trim()) {
      setSnackbar({
        open: true,
        message: tt('Konu ve mesaj zorunludur.', 'Subject and message are required.'),
        severity: 'error'
      });
      return;
    }
    setCreateSaving(true);
    try {
      const res = await ecommerceApi.post('/api/ecommerce/support-tickets', {
        memberId: createForm.memberId || null,
        subject: createForm.subject.trim(),
        message: createForm.message.trim(),
        category: createForm.category,
        priority: createForm.priority,
        contactName: createForm.contactName.trim() || null,
        contactEmail: createForm.contactEmail.trim() || null,
        contactPhone: createForm.contactPhone.trim() || null,
        orderId: createForm.orderId.trim() || null
      });
      if (res.data?.success) {
        setCreateOpen(false);
        setSnackbar({ open: true, message: tt('Destek talebi oluşturuldu.', 'Support ticket created.'), severity: 'success' });
        loadTickets();
      } else {
        throw new Error(res.data?.message);
      }
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || tt('Talep oluşturulamadı.', 'Failed to create ticket.'),
        severity: 'error'
      });
    } finally {
      setCreateSaving(false);
    }
  };

  const filteredCount = totalCount;
  const pagedTickets = tickets;

  if (!companyId) {
    return <Alert severity="warning">{tt('Şirket seçilmedi.', 'No company selected.')}</Alert>;
  }

  return (
    <Box>
      <Paper sx={{ p: 2.5, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <SupportAgentIcon color="primary" />
            <Box>
              <Typography variant="h6" fontWeight={700}>
                {tt('Destek Talepleri', 'Support Tickets')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {tt('E-ticaret müşteri destek taleplerini yönetin', 'Manage e-commerce customer support tickets')}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={supportEnabled}
                  onChange={(e) => handleSupportEnabledChange(e.target.checked)}
                  disabled={supportSettingLoading || supportSettingSaving || !companyId}
                  color="primary"
                />
              }
              label={
                <Box sx={{ pr: 1 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {supportEnabled
                      ? tt('Vitrinde açık', 'Visible on storefront')
                      : tt('Vitrinde kapalı', 'Hidden on storefront')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {tt(
                      'Kapatınca e-ticarette Destek Taleplerim menüsü gizlenir',
                      'When off, Support Tickets menu is hidden in e-commerce'
                    )}
                  </Typography>
                </Box>
              }
              labelPlacement="start"
              sx={{ mr: 1, ml: 0 }}
            />
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadTickets} disabled={loading}>
              {tt('Yenile', 'Refresh')}
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
              {tt('Yeni Talep', 'New Ticket')}
            </Button>
          </Box>
        </Box>
        {!supportEnabled && (
          <Alert severity="info" sx={{ mt: 2 }}>
            {tt(
              'Destek vitrinde kapalı. Müşteriler Destek Taleplerim sayfasını ve menü linkini göremez; panelden talepleri yönetmeye devam edebilirsiniz.',
              'Support is off on the storefront. Customers will not see Support Tickets; you can still manage tickets here.'
            )}
          </Alert>
        )}
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            label={tt('Ara', 'Search')}
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
            sx={{ minWidth: 220 }}
            placeholder={tt('No, konu, müşteri...', 'No, subject, customer...')}
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>{tt('Durum', 'Status')}</InputLabel>
            <Select
              label={tt('Durum', 'Status')}
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
            >
              <MenuItem value="all">{tt('Tümü', 'All')}</MenuItem>
              {STATUSES.map((s) => (
                <MenuItem key={s} value={s}>{statusLabel(s)}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      <Paper>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : tickets.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              {tt('Henüz destek talebi yok. Yeni talep oluşturabilirsiniz.', 'No support tickets yet. You can create a new ticket.')}
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{tt('Talep No', 'Ticket No')}</TableCell>
                    <TableCell>{tt('Müşteri', 'Customer')}</TableCell>
                    <TableCell>{tt('Konu', 'Subject')}</TableCell>
                    <TableCell>{tt('Kategori', 'Category')}</TableCell>
                    <TableCell>{tt('Durum', 'Status')}</TableCell>
                    <TableCell>{tt('Öncelik', 'Priority')}</TableCell>
                    <TableCell>{tt('Tarih', 'Date')}</TableCell>
                    <TableCell align="right">{tt('İşlem', 'Action')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedTickets.map((ticket) => (
                    <TableRow key={ticket.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{ticket.ticketNumber}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{ticket.customerName || '-'}</Typography>
                        {ticket.contactEmail && (
                          <Typography variant="caption" color="text.secondary">{ticket.contactEmail}</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 240 }}>{ticket.subject}</Typography>
                      </TableCell>
                      <TableCell>{categoryLabel(ticket.category)}</TableCell>
                      <TableCell>
                        <Chip size="small" color={STATUS_COLORS[ticket.status] || 'default'} label={statusLabel(ticket.status)} />
                      </TableCell>
                      <TableCell>{priorityLabel(ticket.priority)}</TableCell>
                      <TableCell>{formatDate(ticket.createdAt)}</TableCell>
                      <TableCell align="right">
                        <Tooltip title={tt('Detay', 'Detail')}>
                          <IconButton size="small" onClick={() => openDetail(ticket)}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filteredCount}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              labelRowsPerPage={tt('Sayfa başına', 'Rows per page')}
            />
          </>
        )}
      </Paper>

      {/* Detail dialog */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedTicket
            ? `${selectedTicket.ticketNumber} — ${selectedTicket.subject}`
            : tt('Talep Detayı', 'Ticket Detail')}
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading || !selectedTicket ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }} flexWrap="wrap">
                <Typography variant="body2"><strong>{tt('Müşteri', 'Customer')}:</strong> {selectedTicket.customerName || '-'}</Typography>
                <Typography variant="body2"><strong>{tt('E-posta', 'Email')}:</strong> {selectedTicket.contactEmail || '-'}</Typography>
                <Typography variant="body2"><strong>{tt('Telefon', 'Phone')}:</strong> {selectedTicket.contactPhone || '-'}</Typography>
                <Typography variant="body2"><strong>{tt('Kategori', 'Category')}:</strong> {categoryLabel(selectedTicket.category)}</Typography>
              </Stack>

              <FormControl size="small" sx={{ minWidth: 220, mb: 2 }}>
                <InputLabel>{tt('Durum', 'Status')}</InputLabel>
                <Select
                  label={tt('Durum', 'Status')}
                  value={selectedTicket.status}
                  disabled={statusSaving}
                  onChange={(e) => handleStatusChange(e.target.value)}
                >
                  {STATUSES.map((s) => (
                    <MenuItem key={s} value={s}>{statusLabel(s)}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>{tt('Mesajlar', 'Messages')}</Typography>
              <Stack spacing={1.5} sx={{ mb: 2, maxHeight: 320, overflowY: 'auto' }}>
                {(selectedTicket.messages || []).length === 0 ? (
                  <Typography variant="body2" color="text.secondary">{tt('Mesaj yok.', 'No messages.')}</Typography>
                ) : (
                  (selectedTicket.messages || []).map((msg) => {
                    const isAdmin = msg.senderType === 'Admin';
                    return (
                      <Paper
                        key={msg.id}
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          bgcolor: isAdmin ? 'rgba(59, 130, 246, 0.08)' : 'rgba(148, 163, 184, 0.12)',
                          alignSelf: isAdmin ? 'flex-end' : 'flex-start',
                          maxWidth: '90%'
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          {msg.senderName || msg.senderType} · {formatDate(msg.createdAt)}
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>{msg.body}</Typography>
                      </Paper>
                    );
                  })
                )}
              </Stack>

              <TextField
                fullWidth
                multiline
                minRows={3}
                label={tt('Yanıt yazın', 'Write a reply')}
                value={replyBody}
                onChange={(e) => setReplyBody(e.target.value)}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>{tt('Kapat', 'Close')}</Button>
          <Button
            variant="contained"
            onClick={handleReply}
            disabled={replySaving || !replyBody.trim() || detailLoading}
          >
            {replySaving ? <CircularProgress size={20} /> : tt('Yanıt Gönder', 'Send Reply')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{tt('Yeni Destek Talebi', 'New Support Ticket')}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <FormControl fullWidth size="small">
              <InputLabel>{tt('Üye (opsiyonel)', 'Member (optional)')}</InputLabel>
              <Select
                label={tt('Üye (opsiyonel)', 'Member (optional)')}
                value={createForm.memberId}
                onChange={(e) => handleMemberSelect(e.target.value)}
              >
                <MenuItem value="">{tt('Misafir / Manuel', 'Guest / Manual')}</MenuItem>
                {members.map((m) => (
                  <MenuItem key={m.id} value={m.id}>
                    {(m.displayName || `${m.firstName || ''} ${m.lastName || ''}`.trim() || m.email)} — {m.email}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              label={tt('Konu', 'Subject')}
              value={createForm.subject}
              onChange={(e) => setCreateForm((p) => ({ ...p, subject: e.target.value }))}
              required
              fullWidth
            />
            <TextField
              size="small"
              label={tt('Mesaj', 'Message')}
              value={createForm.message}
              onChange={(e) => setCreateForm((p) => ({ ...p, message: e.target.value }))}
              required
              fullWidth
              multiline
              minRows={3}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <FormControl fullWidth size="small">
                <InputLabel>{tt('Kategori', 'Category')}</InputLabel>
                <Select
                  label={tt('Kategori', 'Category')}
                  value={createForm.category}
                  onChange={(e) => setCreateForm((p) => ({ ...p, category: e.target.value }))}
                >
                  {CATEGORIES.map((c) => (
                    <MenuItem key={c} value={c}>{categoryLabel(c)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel>{tt('Öncelik', 'Priority')}</InputLabel>
                <Select
                  label={tt('Öncelik', 'Priority')}
                  value={createForm.priority}
                  onChange={(e) => setCreateForm((p) => ({ ...p, priority: e.target.value }))}
                >
                  {PRIORITIES.map((p) => (
                    <MenuItem key={p} value={p}>{priorityLabel(p)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
            <TextField
              size="small"
              label={tt('İletişim adı', 'Contact name')}
              value={createForm.contactName}
              onChange={(e) => setCreateForm((p) => ({ ...p, contactName: e.target.value }))}
              fullWidth
            />
            <TextField
              size="small"
              label={tt('E-posta', 'Email')}
              value={createForm.contactEmail}
              onChange={(e) => setCreateForm((p) => ({ ...p, contactEmail: e.target.value }))}
              fullWidth
            />
            <TextField
              size="small"
              label={tt('Telefon', 'Phone')}
              value={createForm.contactPhone}
              onChange={(e) => setCreateForm((p) => ({ ...p, contactPhone: e.target.value }))}
              fullWidth
            />
            <TextField
              size="small"
              label={tt('Sipariş ID (opsiyonel)', 'Order ID (optional)')}
              value={createForm.orderId}
              onChange={(e) => setCreateForm((p) => ({ ...p, orderId: e.target.value }))}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>{tt('İptal', 'Cancel')}</Button>
          <Button variant="contained" onClick={handleCreate} disabled={createSaving}>
            {createSaving ? <CircularProgress size={20} /> : tt('Oluştur', 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SupportManagement;
